import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { acquisitionsApi } from "@/lib/api/acquisitions";
import { Loader2, Save, Info } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

export default function VendorFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    contactPerson: "",
    currency: "NGN",
    taxRate: "",
    discountRate: "",
    avgDeliveryDays: "",
    notes: "",
    isActive: true,
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    acquisitionsApi.vendors
      .getById(id)
      .then((v) => {
        setForm({
          name: v.name,
          code: v.code,
          address: v.address || "",
          phone: v.phone || "",
          email: v.email || "",
          website: v.website || "",
          contactPerson: v.contactPerson || "",
          currency: v.currency,
          taxRate: v.taxRate != null ? String(v.taxRate) : "",
          discountRate: v.discountRate != null ? String(v.discountRate) : "",
          avgDeliveryDays: v.avgDeliveryDays != null ? String(v.avgDeliveryDays) : "",
          notes: v.notes || "",
          isActive: v.isActive,
        });
      })
      .catch(() => toast.error("Failed to load vendor."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) {
      toast.error("Name and code are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        address: form.address || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        website: form.website || undefined,
        contactPerson: form.contactPerson || undefined,
        currency: form.currency || "NGN",
        taxRate: form.taxRate ? parseFloat(form.taxRate) : undefined,
        discountRate: form.discountRate ? parseFloat(form.discountRate) : undefined,
        avgDeliveryDays: form.avgDeliveryDays ? parseInt(form.avgDeliveryDays, 10) : undefined,
        notes: form.notes || undefined,
        isActive: form.isActive,
      };

      if (isEdit && id) {
        await acquisitionsApi.vendors.update(id, payload);
        toast.success("Vendor updated.");
      } else {
        await acquisitionsApi.vendors.create(payload);
        toast.success("Vendor created.");
      }
      navigate("/acquisitions/vendors");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to save vendor."));
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: any) => setForm((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={isEdit ? "Edit Vendor" : "Add Vendor"}
        description={isEdit ? "Update vendor details" : "Register a new supplier"}
        breadcrumbItems={[
          { label: "Vendors", href: "/acquisitions/vendors" },
          { label: isEdit ? "Edit Vendor" : "Add Vendor" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Alert variant="info" className="items-start">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Vendors cannot be deleted. To stop using a supplier, turn off <strong>Active</strong> to
          deactivate the record and hide it from default lists.
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="rounded-lg border bg-white p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Company Information</h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label required>Vendor Name</Label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Academic Books Ltd"
              />
            </div>
            <div className="space-y-1.5">
              <Label required>Vendor Code</Label>
              <Input
                value={form.code}
                onChange={(e) => update("code", e.target.value.toUpperCase())}
                placeholder="e.g. ABL"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Address</Label>
            <Textarea
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              rows={2}
              placeholder="Full mailing address"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="e.g. +234 800 000 0000"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="vendor@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Person</Label>
              <Input
                value={form.contactPerson}
                onChange={(e) => update("contactPerson", e.target.value)}
                placeholder="Primary contact name"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Ordering Terms</h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input
                value={form.currency}
                onChange={(e) => update("currency", e.target.value.toUpperCase())}
                placeholder="NGN"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tax Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.taxRate}
                onChange={(e) => update("taxRate", e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Discount Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.discountRate}
                onChange={(e) => update("discountRate", e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Average Delivery (days)</Label>
            <Input
              type="number"
              value={form.avgDeliveryDays}
              onChange={(e) => update("avgDeliveryDays", e.target.value)}
              placeholder="e.g. 14"
              className="max-w-[200px]"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Notes & Status</h3>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              placeholder="Internal notes about this vendor"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={form.isActive} onCheckedChange={(v) => update("isActive", v)} />
            <Label>Active</Label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/acquisitions/vendors")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEdit ? "Update Vendor" : "Create Vendor"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
